import { isStripeCheckout } from '../../../lib/checkout-mode.js';
import { useState } from 'react';
import { 
  LayoutGrid, 
  ShoppingCart, 
  Menu, 
  Phone, 
  Search, 
  X,
  Bot,
  LayoutDashboard
} from 'lucide-react';
import { Button } from '../../../components/ui/Button.jsx';
import { TenantLogo } from '../../../components/tenant/TenantLogo.jsx';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../../components/ui/Sheet.jsx';
import { useTenant } from '../../../context/TenantContext.jsx';
import { showInternalTools } from '../../../lib/internal-tools.js';
import { resolveMenuRoots } from '../../../lib/db-category-tree.js';
import { useAslCategoryTree } from '../../../lib/use-asl-category-tree.js';
import { isPlainLeftClick } from '../../../lib/click.js';
import { MobileAslNode } from './MobileAslNode.jsx';
import { buildRouteUrl } from '../../../lib/router.js';
import { isAslCategoryActive } from '../../../lib/asl-tree.js';
import { cn } from '../../../lib/utils.js';

export function CenteredBrandHeader({
  tenant,
  cartCount = 0,
  onOpenCart,
  onNavigate,
  onSearch,
  currentPath = '/',
  onOpenCopilot,
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Demo-Tenants: interne Einstiege (Hub, ALWIS) ausblenden (qa-only F-01)
  let ctxTenant = null;
  try {
    const ctx = useTenant();
    ctxTenant = ctx?.tenant || null;
  } catch {
    // außerhalb eines TenantProvider (Tests, isoliertes Rendering)
  }
  const showInternal = showInternalTools((tenant || ctxTenant)?.settings);
  const { tree: aslCategoryTree = [], categories: dbCategories = [], status: aslCategoryTreeStatus } = useAslCategoryTree(tenant?.id ?? ctxTenant?.id);
  const aslMenuRoots = resolveMenuRoots({ aslTree: aslCategoryTree, aslStatus: aslCategoryTreeStatus, dbCategories });

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (onSearch) onSearch(query);
    if (onNavigate && query) {
      onNavigate(`/catalog?search=${encodeURIComponent(query)}`);
      setSearchOpen(false);
    }
  };

  const handleNav = (path) => {
    if (onNavigate) onNavigate(path);
  };

  const categoryUrl = (category) => category.url || buildRouteUrl({ name: 'catalog', aslCategory: category.key });
  const handleAslCategoryClick = (event, category) => {
    if (!onNavigate) return;
    // Strg/Cmd/Umschalt/Alt, Mittelklick: Browser öffnet neuen Tab bzw. Fenster, die App navigiert nicht.
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    handleNav(categoryUrl(category));
  };
  const displayCartCount = cartCount > 99 ? '99+' : String(cartCount);

  return (
    <header
      data-header-variant="centered"
      className="sticky top-0 z-40 w-full border-b border-[var(--border)] bg-white/95 backdrop-blur-md shadow-2xs"
    >
      <div className="mx-auto flex h-20 w-full max-w-[1440px] items-center justify-between px-4 sm:px-6 lg:px-8">
        
        {/* Left Navigation Group */}
        <nav aria-label="Linke Navigation" className="hidden lg:flex items-center gap-1.5 flex-1 justify-start">
          {showInternal && (
          <Button
            variant={currentPath === '/hub' || currentPath.startsWith('/hub') ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => handleNav('/hub')}
            className="h-9 px-3 text-xs font-bold gap-1.5 text-[var(--foreground)]"
            data-testid="executive-hub-header-trigger"
            aria-label="Betriebsleiter Hub öffnen"
          >
            <LayoutDashboard className="h-3.5 w-3.5 text-[var(--primary)]" />
            <span>Betriebsleiter Hub</span>
          </Button>
          )}

          <Button
            variant={currentPath === '/catalog' || currentPath.startsWith('/catalog') ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => handleNav('/catalog')}
            className="h-9 px-3 text-xs font-bold gap-1.5"
          >
            <LayoutGrid className="h-3.5 w-3.5 text-[var(--primary)]" />
            <span>Katalog</span>
          </Button>

          {aslMenuRoots.slice(0, 3).map((cat) => {
            const isCatActive = isAslCategoryActive(currentPath, cat.key, aslMenuRoots);
            return (
              <Button
                key={cat.key}
                data-asl-category-key={cat.key}
                data-asl-level={cat.level}
                variant={isCatActive ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => handleNav(categoryUrl(cat))}
                className="h-9 max-w-40 truncate px-3 text-xs font-semibold"
              >
                {cat.name}
              </Button>
            );
          })}
        </nav>

        {/* Mobile Hamburger (Left on mobile) */}
        <div className="lg:hidden flex items-center">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px]" aria-label="Menü öffnen">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] sm:w-[360px] overflow-y-auto">
              <SheetHeader className="mb-4 text-center">
                <SheetTitle className="flex justify-center font-display">
                  <TenantLogo tenant={tenant} showSlogan={false} />
                </SheetTitle>
              </SheetHeader>

              <div className="space-y-4">
                <form onSubmit={handleSearchSubmit} className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
                  <input
                    type="search"
                    placeholder="Katalog nach SKU durchsuchen..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-9 w-full rounded-[var(--radius)] border border-[var(--border)] pl-9 pr-3 text-xs"
                  />
                </form>

                <div className="space-y-1">
                  <SheetClose asChild>
                    <button
                      onClick={() => handleNav('/catalog')}
                      className="flex w-full items-center gap-3 rounded-[var(--radius)] px-3 py-2 text-left text-xs font-bold hover:bg-[var(--surface-container-low)] cursor-pointer"
                    >
                      <LayoutGrid className="h-4 w-4 text-[var(--primary)]" />
                      Gesamtes Sortiment
                    </button>
                  </SheetClose>

                  {showInternal && (
                  <SheetClose asChild>
                    <button
                      onClick={() => handleNav('/hub')}
                      data-testid="executive-hub-mobile-trigger"
                      className="flex w-full items-center gap-3 rounded-[var(--radius)] px-3 py-2 text-left text-xs font-bold text-[var(--primary)] hover:bg-[var(--surface-container-low)] cursor-pointer"
                    >
                      <LayoutDashboard className="h-4 w-4 text-[var(--primary)]" />
                      Betriebsleiter Hub
                    </button>
                  </SheetClose>
                  )}

                  <div className="my-2 border-t border-[var(--border)]" />

                  {aslMenuRoots.map((category) => (
                    <MobileAslNode
                      key={category.key}
                      category={category}
                      categoryUrl={categoryUrl}
                      onCategoryClick={handleAslCategoryClick}
                      wrapLink={(anchor) => <SheetClose asChild>{anchor}</SheetClose>}
                    />
                  ))}
                </div>

                {tenant?.phone && (
                  <div className="border-t border-[var(--border)] pt-4 text-center">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Persönliche Beratung</p>
                    <a
                      href={`tel:${tenant.phone.replace(/[^0-9+]/g, '')}`}
                      className="inline-flex items-center gap-2 text-xs font-bold text-[var(--primary)]"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {tenant.phone}
                    </a>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Center: Prominent Centered Brand Logo */}
        <div className="flex justify-center items-center shrink-0 px-4">
          <button
            onClick={() => handleNav('/')}
            className="flex flex-col items-center text-center transition-transform hover:scale-[1.02] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] rounded py-1"
            aria-label="Startseite aufrufen"
          >
            <TenantLogo tenant={tenant} showSlogan={false} />
          </button>
        </div>

        {/* Right Navigation & Cart Group */}
        <div className="flex items-center justify-end gap-2 flex-1">
          
          {/* Search Toggle / Input */}
          <div className="relative">
            {searchOpen ? (
              <form onSubmit={handleSearchSubmit} className="flex items-center gap-1">
                <input
                  type="search"
                  autoFocus
                  placeholder="SKU oder Begriff..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-8 w-40 sm:w-56 rounded-full border border-[var(--border)] bg-white px-3 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  onClick={() => setSearchOpen(false)}
                  aria-label="Suche schließen"
                >
                  <X className="h-4 w-4" />
                </Button>
              </form>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchOpen(true)}
                className="h-9 px-2.5 text-xs font-semibold gap-1.5 text-[var(--foreground)]"
                aria-label="Suche öffnen"
              >
                <Search className="h-3.5 w-3.5 text-[var(--primary)]" />
                <span className="hidden sm:inline">Suche</span>
              </Button>
            )}
          </div>

          {/* Hotline */}
          {tenant?.phone && (
            <a
              href={`tel:${tenant.phone.replace(/[^0-9+]/g, '')}`}
              className="hidden xl:flex items-center gap-1.5 rounded-full border border-[var(--border)] px-3 py-1.5 text-xs font-semibold text-[var(--foreground)] hover:bg-[var(--surface-container-low)] transition-colors"
            >
              <Phone className="h-3 w-3 text-[var(--primary)]" />
              <span>{tenant.phone}</span>
            </a>
          )}

          {/* Copilot Assistant Trigger (nur ASL-intern) */}
          {showInternal && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="copilot-header-trigger"
            onClick={onOpenCopilot}
            className="h-9 gap-1.5 px-2.5 text-xs font-bold text-[var(--foreground)] hover:bg-[var(--surface-container-low)] cursor-pointer"
            aria-label="ALWIS Fachassistent öffnen"
          >
            <Bot className="h-3.5 w-3.5 text-[var(--primary)]" />
            <span className="hidden sm:inline">ALWIS</span>
          </Button>
          )}

          {/* Cart Button */}
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              if (onOpenCart) onOpenCart();
              else handleNav('/cart');
            }}
            className="h-9 gap-1.5 px-3.5 text-xs font-bold shadow-xs cursor-pointer"
            aria-label={`${isStripeCheckout(tenant || ctxTenant) ? 'Warenkorb' : 'Anfragekorb'} (${cartCount})`}
          >
            <ShoppingCart className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{isStripeCheckout(tenant || ctxTenant) ? 'Warenkorb' : 'Anfragekorb'}</span>
            <span
              data-testid="cart-count-badge"
              className={cn(
                'flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-xs font-bold leading-none transition-colors',
                cartCount > 0
                  ? 'bg-white text-[var(--primary,#e3000b)] font-extrabold shadow-2xs'
                  : 'bg-white/30 text-white'
              )}
            >
              {displayCartCount}
            </span>
          </Button>

        </div>
      </div>
    </header>
  );
}
